import jsPDF from "jspdf";

const formatNumber = (number) => {
  if (number == undefined || number === 0) {
    return "0.00";
  }
  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number)
    .split(".");

  if (numberFormat.length == 1) {
    numberFormat.push("00");
  }
  return (
    numberFormat[0] +
    "." +
    (parseInt(numberFormat[1]) < 10 && parseInt(numberFormat[1]) > 0
      ? "0" + parseInt(numberFormat[1])
      : numberFormat[1])
  );
}

function sumChildrenAmounts(node) {
  let yearTotals = { total: 0 };

  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      const { yearTotals: childYearTotals } = sumChildrenAmounts(child);
      for (const key in childYearTotals) {
        if (!yearTotals[key]) yearTotals[key] = 0;
        yearTotals[key] += childYearTotals[key];
      }
    });
  } else {
    yearTotals = node.yearTotals || { total: 0 };
  }

  node.yearTotals = yearTotals;

  return { yearTotals };
}

function flattenTree(data, level = 0, result = []) {
  data.forEach(node => {
    const alwaysInclude = [2, 3, 4].includes(node.id);
    const hasNonZeroTotals = Object.values(node.yearTotals).some(total => total !== 0);

    if (alwaysInclude || hasNonZeroTotals) {
      result.push({
        id: node.id,
        name: node.name,
        parent: node.parent,
        code: node.code,
        note: node.note,
        account_type_id: node.account_type_id,
        dgii_category_id: node.dgii_category_id,
        status: node.status,
        level: level,
        yearTotals: node.yearTotals,
        isParent: alwaysInclude
      });

      if (node.children && node.children.length > 0) {
        flattenTree(node.children, level + 1, result);
      }
    }
  });
  return result;
}

export const generateEstadoSituacionPDF = async (all_accounts, processed_accounts, info, accumulatedAmounts) => {
  const addAmountsToAccounts = (accounts, processed) => {
    accounts.forEach(account => {
      let yearTotals = { total: 0 };

      processed.forEach(procAccount => {
        if (procAccount.chart_of_account_id === account.id) {
          const yearTotal = parseFloat(procAccount.year_total) || 0;
          yearTotals.total += yearTotal;
        }
      });

      account.yearTotals = yearTotals;

      if (account.children && account.children.length > 0) {
        addAmountsToAccounts(account.children, processed);
      }
    });
  };

  addAmountsToAccounts(all_accounts, processed_accounts);

  all_accounts.forEach(node => sumChildrenAmounts(node));

  const flattenedData = flattenTree(all_accounts);

  const doc = new jsPDF({
    orientation: "portrait",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const rowHeight = 10;
  const headerHeight = 40;
  const maxRowsPerPage = Math.floor((doc.internal.pageSize.getHeight() - headerHeight - margin) / rowHeight);

  let startY = margin + headerHeight;
  let rowCount = 0;
  const accountNameWidth = 100;
  const totalColumnPosition = pageWidth - margin;

  const addPageHeader = () => {
    doc.setFontSize(14);
    doc.text(info.business_name, pageWidth / 2, margin + 10, { align: 'center' });
    doc.text('Estado de Situación Financiera', pageWidth / 2, margin + 20, { align: 'center' });
    doc.text(`Valor expresado en (${info.currency_code})`, pageWidth / 2, margin + 30, { align: 'center' });
    doc.text(`Hasta la fecha ${info.date_to}`, pageWidth / 2, margin + 40, { align: 'center' });

    doc.setFontSize(12);
    startY = margin + headerHeight + 20;
    doc.text('Código cuenta', margin, startY);
    doc.text('Nombre cuenta', margin + 40, startY);
    doc.text('Monto Acumulado', totalColumnPosition, startY, { align: 'right' });
    startY += rowHeight;
  };

  addPageHeader();

  const addRow = (row, nature = 1) => {
    if (rowCount >= maxRowsPerPage) {
      doc.addPage();
      addPageHeader();
      rowCount = 0;
    }

    const indent = ' '.repeat(row.level * 4);
    doc.text(row.code || '', margin, startY);
    doc.text(indent + row.name || '', margin + 40, startY, { maxWidth: accountNameWidth });
    // Mismo criterio que la vista/Excel: naturaleza solo en cuentas raiz (activos +1,
    // pasivos/patrimonio -1); los hijos llegan con nature=1 (crudo)
    const total = Object.values(row.yearTotals).reduce((sum, value) => sum + value, 0) * nature;
    doc.text(formatNumber(parseFloat(total).toFixed(2)), totalColumnPosition, startY, { align: 'right' });

    startY += rowHeight;
    rowCount++;
  };

  // Naturaleza solo en las cuentas raiz; los hijos van en crudo (igual que la vista)
  flattenedData.forEach((row) => {
    const nature = row.isParent ? (row.id === 2 ? 1 : -1) : 1;
    addRow(row, nature);
  });

  const addTotalRow = (title, total) => {
    if (rowCount >= maxRowsPerPage) {
      doc.addPage();
      addPageHeader();
      rowCount = 0;
    }

    startY += rowHeight * 2;
    doc.text(title, margin + 40, startY, { maxWidth: accountNameWidth });
    doc.text(formatNumber(parseFloat(total).toFixed(2)), totalColumnPosition, startY, { align: 'right' });

    startY += rowHeight;
    rowCount++;
  };

  // Calculos en crudo (signo real) e invertidos (-1) solo al mostrar, igual que la vista/Excel
  const accumulatedTotal = Object.values(accumulatedAmounts).reduce((sum, value) => sum + value, 0);
  addTotalRow('Utilidad Periodo Actual', accumulatedTotal * -1);

  const patrimonioNode = all_accounts.find(node => node.id === 4);
  const pasivosNode = all_accounts.find(node => node.id === 3);

  const totalParticipacion = Object.values(patrimonioNode.yearTotals).reduce((sum, value) => sum + value, 0) + accumulatedTotal;
  addTotalRow('TOTAL PARTICIPACIÓN', totalParticipacion * -1);

  const totalPasivosParticipacion = totalParticipacion + Object.values(pasivosNode.yearTotals).reduce((sum, value) => sum + value, 0);
  addTotalRow('TOTAL PASIVOS Y PARTICIPACIÓN', totalPasivosParticipacion * -1);

  doc.save(`Estado de Situación - ${info.business_name} - Hasta la fecha ${info.date_to}.pdf`);
};
